/// <amd-module name="Wit/WorkItems/Redux/Sagas/WorkItemChangeSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { RT } from "redux/Utils/SagaHelper";
import { IVssPageContext } from "VSS/Platform/Context";
import { Level } from "VSS/Platform/Contracts/ClientTrace";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { trace } from "VSS/Platform/Trace";
import { IWorkItemChange } from "Wit/WorkItems/WorkItemContracts";
import { getWorkItemTypeFields } from "Wit/WorkItemTypes/Selectors/WorkItemTypeSelectors";
import { IFieldUpdateRequest, updateWorkItemAsync } from "../../DataSources/WorkItemDataSource";
import { Area, Feature } from "../../TelemetryConstants";
import { areFieldValuesDifferent, transformFieldReferenceNames } from "../../Utilities/FieldUtilities";
import { splitWorkItemRelations } from "../../Utilities/WorkItemRelationUtilities";
import { isWorkItemNew } from "../../Utilities/WorkItemUtilities";
import { WorkItemActions, WorkItemActionTypes } from "../../WorkItemActions";
import { IWorkItem, WorkItemStatus } from "../../WorkItemContracts";
import { getFieldValue, getFieldValueWithoutPendingChanges, getWorkItem, getWorkItemProjectId, getWorkItemTypeName } from "../../WorkItemSelectors";
import { getNextPendingWorkItemChange, getPendingWorkItemChanges } from "../Selectors";

const FieldSaveScenarioName = "WorkItemFieldSave";

/** List of active workers per work item */
const workers: { [workItemId: number]: boolean } = {};

export function* workItemChangeProcessor(action: ActionsOfType<WorkItemActions, WorkItemActionTypes.WorkItemChangeRequested>): SagaIterator {
    const { workItemId } = action.payload;

    // If any change needs to be done queue a server change
    // We don't want to auto save workitem on field change in work item create scenario.
    if (!isWorkItemNew(workItemId) && !workers[workItemId]) {
        // Queue up a combined server request serially if anything was updated.
        yield effects.spawn(changeWorkItemWorker, workItemId);
    }
}

/**
 * Processes save requests and responses to the server serially for a single work item
 */
function* changeWorkItemWorker(workItemId: number): SagaIterator {
    // Mark this worker as active
    workers[workItemId] = true;

    let errorOccured = false;

    // Mark the work item as being saved
    yield effects.put(WorkItemActions.updateWorkItemsStatus([{ workItemId, status: WorkItemStatus.Saving }]));

    for (;;) {
        // Get next change in queue
        const requestedWorkItemChange: RT<typeof getNextPendingWorkItemChange> = (yield effects.select(
            getNextPendingWorkItemChange,
            workItemId
        )) as any;
        if (!requestedWorkItemChange) {
            // No more pending changes
            break;
        }

        if (requestedWorkItemChange.isBlocked) {
            // If the action is blocked, wait for an unblocking action before proceeding
            yield effects.take(
                (action: ActionsOfType<WorkItemActions, WorkItemActionTypes.UnblockWorkItemChange>) =>
                    action.type === WorkItemActionTypes.UnblockWorkItemChange && action.payload.workItemId === workItemId
            );
        }

        try {
            const workItem: RT<typeof getWorkItem> = (yield effects.select(getWorkItem, workItemId)) as any;
            if (!workItem) {
                // The work item has been removed from the store just abort
                break;
            }

            // Generate the combined update document, save, and process save
            yield effects.call(saveWorkItemAndProcessResponse, workItem, requestedWorkItemChange, requestedWorkItemChange.scenarioStart);
        } catch (error) {
            yield effects.spawn(sendFailureTelemetry, workItemId, error);

            // Handle the error
            yield effects.put(WorkItemActions.setWorkItemError(workItemId, (error as Error).message));

            errorOccured = true;
            break;
        }
    }

    if (!errorOccured) {
        // Reset work item status
        yield effects.put(WorkItemActions.updateWorkItemsStatus([{ workItemId, status: WorkItemStatus.Loaded }]));
    }

    // Processed all changes, close channel.
    delete workers[workItemId];
}

/** NOTE: this should not catch errors */
function* saveWorkItemAndProcessResponse(workItem: IWorkItem, workItemChange: IWorkItemChange, scenarioStart?: number): SagaIterator {
    const { id: workItemId } = workItem;
    const { fieldChanges, linkChanges } = workItemChange;

    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const workItemTypeName: RT<typeof getWorkItemTypeName> = (yield effects.select(getWorkItemTypeName, workItemId)) as any;
    const projectId: RT<typeof getWorkItemProjectId> = (yield effects.select(getWorkItemProjectId, workItemId)) as any;
    const typeFields: RT<typeof getWorkItemTypeFields> = (yield effects.select(getWorkItemTypeFields, projectId, workItemTypeName)) as any;
    if (!typeFields) {
        throw new Error("TypeFields need to be available for sending the work item");
    }

    let dirtyFields: { [fieldRefName: string]: IFieldUpdateRequest } | undefined;
    if (fieldChanges) {
        dirtyFields = {};
        for (const change of fieldChanges) {
            const originalValue: RT<typeof getFieldValue> = (yield effects.select(
                getFieldValueWithoutPendingChanges,
                workItem.id,
                change.fieldRefName
            )) as any;
            if (areFieldValuesDifferent(typeFields[change.fieldRefName.toLocaleLowerCase()], originalValue, change.value)) {
                dirtyFields[change.fieldRefName] = { ...change, originalValue };
            }
        }
    }

    // Save the link and/or field changes - if there are changes
    if ((!!dirtyFields && Object.keys(dirtyFields).length > 0) || !!linkChanges) {
        const updateResult: RT<typeof updateWorkItemAsync> = (yield effects.call(
            updateWorkItemAsync,
            pageContext,
            workItem.id,
            workItem.projectId,
            dirtyFields,
            linkChanges
        )) as any;

        const { workItem: updatedWorkItemModel, fieldFlags } = updateResult;

        if (fieldFlags) {
            yield effects.put(WorkItemActions.loadFieldFlagsSucceeded(workItem.id, fieldFlags));
        }

        // Update the work item state with the updated data from the server
        yield effects.put(
            WorkItemActions.workItemUpdated(
                workItem.id,
                updatedWorkItemModel.fields && transformFieldReferenceNames(updatedWorkItemModel.fields),
                updatedWorkItemModel.relations && splitWorkItemRelations(updatedWorkItemModel.relations)
            )
        );
    }

    // Tell consumers about the succcessful change
    yield effects.put(WorkItemActions.workItemChangeSucceeded(workItem.id, workItemChange));

    // Record telemetry
    if (scenarioStart) {
        // Send telemetry without blocking the worker
        yield effects.spawn(sendUpdateTelemetry, pageContext, scenarioStart, workItemChange);
    }
}

function* sendUpdateTelemetry(pageContext: IVssPageContext, scenarioStart: number, workItemChange: IWorkItemChange): SagaIterator {
    const { fieldChanges, linkChanges } = workItemChange;

    const performanceService: IVssPerformanceService = (yield effects.call([pageContext, pageContext.getService], "IVssPerformanceService")) as any;

    yield effects.call([performanceService, performanceService.startScenario], FieldSaveScenarioName, false, scenarioStart);
    yield effects.call([performanceService, performanceService.endScenario], Area.WIT, FieldSaveScenarioName, false, undefined, {
        isSuccessful: true,
        fieldRefNames: fieldChanges ? fieldChanges.map(f => f.fieldRefName).join(";") : "",
        linkTypes: linkChanges ? linkChanges.map(l => l.referenceName).join(";") : ""
    });
}

function* sendFailureTelemetry(workItemId: number, error: Error): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const pendingChanges: RT<typeof getPendingWorkItemChanges> = (yield effects.select(getPendingWorkItemChanges, workItemId)) as any;

    yield effects.call(trace, pageContext, {
        area: Area.WIT,
        feature: Feature.WorkItem,
        level: Level.Info,
        component: "WorkItemChangeWorker",
        exceptionType: "",
        message: "Optimistic save failed, reverting field and link changes",
        method: "",
        properties: {
            action: WorkItemActionTypes.WorkItemChangeRequested,
            revertedFieldChanges: pendingChanges && pendingChanges.reduce((r, x) => r + (x.fieldChanges || []).length, 0),
            revertedLinkChanges: pendingChanges && pendingChanges.reduce((r, x) => r + (x.linkChanges || []).length, 0),
            error
        }
    });
}
